import { useCustomForm } from "common/hooks/src/api/useCustomForm";
import { Button } from "components/button";
import { CardWithTitle } from "components/card/CardWithTitle";
import { InputField, ResizableInputField } from "components/fields/InputField";
import {
  useArchiveCommande,
  useDeleteDocument,
  useGetCommande,
  useUpdateCommande,
  useUploadDocument,
} from "modules/commande/api";
import {
  CreateCommandePayload,
  ReductionType,
  CommandeLineItem,
} from "modules/commande/types/commande.interface";
import { FormEvent, useEffect } from "react";
import { useDocumentPreview } from "common/hooks/src/useDocumentPreview";
import "react-datepicker/dist/react-datepicker.css";
import { Link, useParams } from "react-router-dom";
import { useGetArticles } from "modules/article/api";
import {
  SelectClient,
  SelectArticle,
  SelectReductionType,
  SelectDevis,
} from "modules/commande/components";
import { INVOICE_STATUS } from "modules/invoice/types/invoice.interface";

const UpdateCommande = () => {
  const { id } = useParams<{ id: string }>();
  const { data: commande, refetch } = useGetCommande(id);
  const { mutate } = useUpdateCommande(id);
  const { mutate: archiveCommande } = useArchiveCommande();
  const { mutate: deleteDocument } = useDeleteDocument(refetch);

  const { data: articlesData } = useGetArticles(undefined);

  const { formValues, setFormValues } = useCustomForm<CreateCommandePayload>({
    clientId: "",
    lineItems: [
      {
        articleId: "",
        priceHT: 0,
        quantity: 0,
        vatAmount: 0,
        reductionAmount: 0,
        reductionType: ReductionType.PERCENTAGE,
        totalTTC: 0,
        totalHT: 0,
        description: "",
        _id: "",
        checked: false,
      },
    ],
    totalTTC: 0,
    totalHT: 0,
    notes: "",
    savedDocuments: [],
    linkedInvoices: [],
  });

  const liveSummary = useDocumentPreview(formValues.lineItems);

  const uploadDocumentMutation = useUploadDocument(refetch);

  const isSubmitDisabled = false;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    mutate(formValues);
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await uploadDocumentMutation.mutateAsync({
        commandeId: id,
        file,
      });
    } catch (error) {
      console.error("Failed to upload document:", error);
    }
  };

  useEffect(() => {
    if (!commande) return;
    {
      const allArticles = articlesData
        ? articlesData.pages.flatMap((page) => page.data)
        : [];

      const updatedLineItems = commande.lineItems.map((item) => {
        const matchedArticle = allArticles.find(
          (a) => a._id === item.articleId
        );

        return {
          ...item,
          type: matchedArticle?.type,
          quantity: Number(item.quantity) || 0,
          vatAmount: Number(item.vatAmount) || 0,
          reductionAmount: Number(item.reductionAmount) || 0,
          reductionType: item.reductionType || ReductionType.PERCENTAGE,
          totalTTC: Number(item.totalTTC) || 0,
          totalHT: Number(item.totalHT) || 0,
          description: item.description ?? "",
          priceHT: (() => {
            const stored = Number(matchedArticle?.priceHT ?? (item as any).unitPriceHT ?? item.priceHT) || 0;
            if (stored > 0) return stored;
            const qty = Number(item.quantity);
            const ht = Number(item.totalHT);
            return !Number(item.reductionAmount) && ht > 0 && qty > 0 ? ht / qty : 0;
          })(),
        };
      });

      setFormValues({
        ...commande,
        lineItems: updatedLineItems,
        totalTTC: Number(commande.totalTTC),
        totalHT: Number(commande.totalHT),
        devisId: commande.devisId,
      });
    }
  }, [commande, articlesData, setFormValues]);

  const handleLineItemChange = (
    index: number,
    field: keyof CommandeLineItem,
    value: any
  ) => {
    const updatedLineItems = formValues.lineItems.map((item, idx) => {
      if (idx === index) {
        const newItem = { ...item, [field]: value };

        if (field === "articleId") {
          const allArticles =
            articlesData?.pages.flatMap((page) => page.data) || [];
          const selectedArticle = allArticles.find((a) => a._id === value);
          if (selectedArticle) {
            newItem.priceHT = selectedArticle.priceHT;
          }
        }

        const priceHT = Number(newItem.priceHT) || 0;

        if (priceHT > 0) {
          const quantity = Number(newItem.quantity) || 0;
          const vatPercent = Number(newItem.vatAmount) || 0;
          const reductionAmount = Number(newItem.reductionAmount) || 0;
          const reductionType = newItem.reductionType;

          const ht = priceHT * quantity;
          const vatAmount = ht * (vatPercent / 100);
          const baseAmount = ht + vatAmount;

          const reduction =
            reductionType === ReductionType.PERCENTAGE
              ? baseAmount * (reductionAmount / 100)
              : reductionAmount;

          const total = Math.max(0, baseAmount - reduction);

          newItem.vatAmount = Number(newItem.vatAmount) || 0;
          newItem.totalTTC = Number(total.toFixed(2));
          newItem.totalHT = Number(ht.toFixed(2));
        }

        return newItem;
      }
      return item;
    });

    // Calcul du nouveau total global
    const newTotalCommande = updatedLineItems.reduce(
      (acc, item) => acc + item.totalTTC,
      0
    );

    const newTotalHT = updatedLineItems.reduce((acc, item) => acc + item.totalHT, 0);

    setFormValues((prev) => ({
      ...prev,
      lineItems: updatedLineItems,
      totalTTC: Number(newTotalCommande.toFixed(2)),
      totalHT: Number(newTotalHT.toFixed(2)),
    }));
  };

  const handleDeleteDocument = async (url: string, commandeId: string) => {
    try {
      await deleteDocument({ url, commandeId });
    } catch (error) {
      console.error("Failed to delete document:", error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <CardWithTitle title="Formulaire Commande">
        <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {formValues.devisId && (
            <SelectDevis
              formValues={formValues}
              setFormValues={setFormValues}
            />
          )}
          <SelectClient formValues={formValues} setFormValues={setFormValues} />
        </div>

        <div className="mt-6">
          <h3 className="mb-4 text-xl font-semibold">Articles</h3>
          {formValues.lineItems.map((item, index) => (
            <div
              key={index}
              className="relative mb-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4"
            >
              <div className="">
                <input
                  type="checkbox"
                  id={`checked-${index}`}
                  checked={item.checked}
                  onChange={(e) =>
                    handleLineItemChange(index, "checked", e.target.checked)
                  }
                  className="mr-2"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
                <div className="col-span-1">
                  <SelectArticle
                    value={item.articleId}
                    onChange={(articleId) =>
                      handleLineItemChange(index, "articleId", articleId)
                    }
                  />
                </div>
                <div className="col-span-1">
                  <InputField
                    variant="auth"
                    label="Prix HT"
                    id={`priceHT-${index}`}
                    type="number"
                    extra="mb-3"
                    placeholder="0.00"
                    value={item.priceHT}
                    onChange={(e) =>
                      handleLineItemChange(
                        index,
                        "priceHT",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div className="col-span-1">
                  <InputField
                    variant="auth"
                    label="Quantité"
                    id={`quantity-${index}`}
                    type="number"
                    extra="mb-3"
                    placeholder="0"
                    value={item.quantity}
                    onChange={(e) =>
                      handleLineItemChange(
                        index,
                        "quantity",
                        parseInt(e.target.value, 10) || 0
                      )
                    }
                  />
                </div>
                <div className="col-span-1">
                  <InputField
                    variant="auth"
                    label="TVA %"
                    id={`vatAmount-${index}`}
                    type="number"
                    extra="mb-3"
                    placeholder="20.00"
                    value={item.vatAmount || ""}
                    onChange={(e) =>
                      handleLineItemChange(
                        index,
                        "vatAmount",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div className="col-span-1">
                  <InputField
                    variant="auth"
                    label="Réduction"
                    id={`reductionAmount-${index}`}
                    type="number"
                    extra="mb-3"
                    placeholder="0.00"
                    value={item.reductionAmount || ""}
                    onChange={(e) =>
                      handleLineItemChange(
                        index,
                        "reductionAmount",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div className="col-span-1">
                  <SelectReductionType
                    value={item.reductionType}
                    onChange={(reductionType) =>
                      handleLineItemChange(
                        index,
                        "reductionType",
                        reductionType
                      )
                    }
                  />
                </div>
                <div className="col-span-1">
                  <InputField
                    variant="auth"
                    label="Total TTC"
                    id={`total-${index}`}
                    type="number"
                    extra="mb-3"
                    placeholder="0.00"
                    value={item.totalTTC}
                    disabled
                  />
                </div>
              </div>
              <div className="mt-4">
                <ResizableInputField
                  variant="auth"
                  label="Description"
                  id={`description-${index}`}
                  extra="mb-3"
                  placeholder="Description de l'item"
                  value={item.description}
                  disabled
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <ResizableInputField
            variant="auth"
            label="Notes"
            id="notes"
            extra="mb-3"
            placeholder="Ajouter des notes"
            value={formValues.notes || ""}
            onChange={(e) =>
              setFormValues({ ...formValues, notes: e.target.value })
            }
          />
        </div>

        <div className="mt-2 flex w-full justify-between"></div>
      </CardWithTitle>
      <CardWithTitle title="Récapitulatif">
        {(() => {
          const s = liveSummary ?? commande?.summary;
          if (!s) return null;
          const fmt = (n: number) =>
            Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + " €";
          const rows: { label: string; value: string; bold?: boolean }[] = [
            { label: "Sous-total HT", value: fmt(s.totalHT) },
            { label: "Remise", value: fmt(s.totalRemise) },
            { label: "Total HT après remise", value: fmt(s.totalHTFinal) },
            { label: `TVA (${s.vatPercentage}%)`, value: fmt(s.totalVAT) },
            { label: "Total TTC", value: fmt(s.totalTTC), bold: true },
          ];
          return (
            <div className="rounded-lg bg-gray-50 p-4">
              {rows.map((row, i) => (
                <div
                  key={row.label}
                  className={`flex items-center justify-between py-2 ${i === rows.length - 1 ? "mt-2 border-t border-gray-200 pt-3" : ""}`}
                >
                  <span className={`text-sm ${row.bold ? "font-semibold text-gray-900" : "text-gray-500"}`}>
                    {row.label}
                  </span>
                  <span className={`text-sm ${row.bold ? "font-bold text-blue-600 text-base" : "font-medium text-gray-700"}`}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          );
        })()}
      </CardWithTitle>
      <CardWithTitle title="Factures liées">
        <div className="space-y-4 p-4">
          {/* Existing Invoices List */}
          {commande?.linkedInvoices && commande.linkedInvoices.length > 0 && (
            <div className="space-y-2">
              {commande.linkedInvoices.map((invoice, index) => (
                <div
                  key={index}
                  className="dark:bg-neutral-800 flex items-center justify-between rounded-lg bg-gray-50 p-3"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-sm">
                      Facture - {invoice.number.number}{" "}
                    </span>
                    {"-"}
                    <p
                      className={`text-sm ${
                        invoice.status === INVOICE_STATUS.PAID
                          ? "text-green-500"
                          : "text-red-500"
                      }`}
                    >
                      {invoice.status === INVOICE_STATUS.PAID
                        ? "Payée"
                        : "Non payée"}
                    </p>
                  </div>
                  <Link
                    to={`/admin/invoice/${invoice._id}`}
                    className="text-sm text-blue-500 underline hover:text-blue-600"
                  >
                    Voir la facture
                  </Link>
                </div>
              ))}
            </div>
          )}

          {/* Create Invoice Button - Always visible */}
          <div className="flex justify-center">
            <Link
              to={`/admin/invoice/create?commandeId=${id}&devisId=${formValues.devisId}`}
              className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
            >
              <svg
                className="size-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Créer une facture
            </Link>
          </div>
        </div>
      </CardWithTitle>
      <CardWithTitle title="Documents">
        <div className="p-4">
          <div className="space-y-4">
            {/* Existing Documents List */}
            {commande?.savedDocuments && commande.savedDocuments.length > 0 && (
              <div className="space-y-2">
                {commande.savedDocuments.map((url, index) => (
                  <div
                    key={index}
                    className="dark:bg-neutral-800 flex items-center justify-between rounded-lg bg-gray-50 p-3"
                  >
                    <span className="text-sm">Document #{index + 1}</span>
                    <div className="flex items-center gap-4">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-500 underline hover:text-blue-600"
                      >
                        {url.toLowerCase().endsWith(".pdf")
                          ? "Voir PDF"
                          : "Voir Image"}
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(url, commande._id)}
                        className="text-red-500 hover:text-red-600"
                        type="button"
                      >
                        <svg
                          className="size-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Button - Always visible */}
            <div className="flex justify-center rounded-lg border-2 border-dashed p-4">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <span className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600">
                  <svg
                    className="size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Ajouter un document
                </span>
              </label>
            </div>
          </div>
        </div>
      </CardWithTitle>
      <div className="mt-2 flex w-full justify-between flex-wrap">
        <div className="w-full max-w-xs mb-2">
          <Button
            onClick={() => archiveCommande(id)}
            disabled={isSubmitDisabled}
            variant="alert"
          >
            Archiver la commande
          </Button>
        </div>
        <div className="w-full max-w-xs mb-2">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            variant="primary"
          >
            Mettre à jour la commande
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UpdateCommande;
